/**
 * The add-client dialog's half of ADR-0033: a client's register details from the
 * GST portal, by two routes the person chooses between.
 *
 * - **Here, with a CAPTCHA** — ProGST's server asks the portal for a CAPTCHA, the
 *   person types what the picture shows, the server searches. The portal sees
 *   ProGST's server.
 * - **On the portal, then paste** — the person searches and solves the CAPTCHA in
 *   their own tab and pastes the result page. The portal sees the person; ProGST's
 *   server contacts nobody.
 *
 * Either way **the CAPTCHA is the person's** and the answer only fills the form:
 * every value stays editable until **Add client** (FR-C14). Pure — the dialog
 * calls these; the tests read them.
 */

import type { RegisterProfile } from './api'

export const REGISTRATION_TYPES = [
  ['regular', 'Regular'],
  ['composition', 'Composition'],
  ['casual', 'Casual taxable person'],
  ['isd', 'Input service distributor'],
  ['sez', 'SEZ unit / developer'],
  ['non_resident', 'Non-resident taxable person'],
] as const

/**
 * The register's word for a registration type, matched against ours — and
 * `null` when it matches nothing. A near-miss must NOT fall back to `regular`:
 * that is the one value whose wrongness silently changes a return, and a
 * composition dealer filed as regular is not a cosmetic error.
 */
export function knownRegistrationType(reported: string | null | undefined): string | null {
  if (!reported) return null
  const wanted = reported.trim().toLowerCase()
  const hit = REGISTRATION_TYPES.find(
    ([value, label]) => value === wanted || label.toLowerCase() === wanted,
  )
  return hit ? hit[0] : null
}

/**
 * Our registration type in words (N29): Client Details read "Registration type: regular", and
 * the Clients list printed the stored value capitalised (`non_resident` → "Non_resident"). An
 * unknown value says it is one, never the code.
 */
export function registrationTypeLabel(value: string | null | undefined): string | null {
  if (!value) return null
  // Matched as the gate reads it (`gating.surfaces_for` trims and lowers), so a type typed
  // "Regular" on Client Details reads as the type it is treated as.
  // Its key only, never its label: the gate does not read "Non-resident taxable person" as
  // `non_resident`, so neither may the screen.
  const wanted = value.trim().toLowerCase()
  const hit = REGISTRATION_TYPES.find(([type]) => type === wanted)
  return hit ? hit[1] : 'A registration type ProGST has no name for'
}

/**
 * The portal's date of registration (`01/07/2017`, day first) as the date the
 * create takes (`2017-07-01`). `null` for anything that is not a real day — a
 * date is parsed here and nowhere else, and a wrong one is worse than none.
 */
export function portalDateToIso(text: string | null | undefined): string | null {
  const m = /^\s*(\d{1,2})[/-](\d{1,2})[/-](\d{4})\s*$/.exec(text ?? '')
  if (!m) return null
  const [day, month, year] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const probe = new Date(Date.UTC(year, month - 1, day))
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** The form the register can fill. Every key is the form's own state. */
export interface FormFill {
  legalName: string
  tradeName: string
  constitution: string
  registrationType: string
  registeredOn: string
  addressLine: string
  city: string
  pincode: string
}

/**
 * The register's answer laid over what the person already has. **Filled, not
 * forced**: a field the answer leaves blank keeps what the person typed — a
 * "prefill" that blanks a typed name is the worst reading of the word.
 */
export function fillFromRegister(current: FormFill, register: RegisterProfile): FormFill {
  return {
    legalName: register.legal_name || current.legalName,
    tradeName: register.trade_name || current.tradeName,
    constitution: register.constitution || current.constitution,
    registrationType: knownRegistrationType(register.registration_type) ?? current.registrationType,
    registeredOn: portalDateToIso(register.registered_on) ?? current.registeredOn,
    addressLine: register.address_line || current.addressLine,
    city: register.city || current.city,
    pincode: register.pincode || current.pincode,
  }
}

/**
 * When the register names a registration type ProGST has no option for, the
 * sentence that says so — the type is then the person's to set, never mapped
 * to `regular` by proximity. `null` when there is nothing to say.
 */
export function registrationTypeNote(register: RegisterProfile): string | null {
  return register.registration_type && !knownRegistrationType(register.registration_type)
    ? `The register reports the registration type as "${register.registration_type}", ` +
        'which is not one of the options here — choose it yourself.'
    : null
}

/** Where a filled value came from, in words — never the route's code. */
export const SOURCE_WORDS: Record<string, string> = {
  gst_portal: 'From the GST portal, fetched through ProGST',
  gst_portal_pasted: 'From the GST portal, pasted from your tab',
}

export function sourceWords(source: string | null | undefined): string {
  return (source && SOURCE_WORDS[source]) || 'From the GST portal'
}

/**
 * **The paste route is parked** (owner, 2026-10-03). E-05's first check found the
 * route through ProGST working against the real portal, and the owner chose to
 * offer that one alone for now. The paste reader has never met a real copied page,
 * so it is kept — the service route, the reader and their tests are all still
 * here — and not offered. Offering it again is this value.
 */
export const PASTE_ROUTE_OFFERED = false

/** The steps for the paste route, as the dialog shows them. */
export const PASTE_STEPS = [
  'Open the portal’s search (the link opens a new tab).',
  'Enter the GSTIN and type the CAPTCHA there.',
  'On the result page, select everything (Ctrl+A) and copy it (Ctrl+C).',
  'Paste it below and choose Read.',
] as const
